import { useEffect, useState } from "react";
import { BellRinging, Megaphone, ShareNetwork, ArrowUp, ArrowDown, CaretDown } from "@phosphor-icons/react";
import api from "../lib/api";
import Sparkline from "./Sparkline";

/**
 * NotifyReachCard — surfaces the owner's "push reach": how many people
 * will receive a `truck_live` push the moment one of their trucks goes
 * live. Pairs the headline number with a per-truck breakdown and a soft
 * sharing nudge so growth stays self-reinforcing.
 *
 * Backend: GET /api/analytics/notify-reach (owner-only).
 */
export default function NotifyReachCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Collapsed by default — owners with many trucks were seeing 20+ rows
  // before the headline. Headline is enough at a glance; expand for the
  // per-truck breakdown + coaching nudge.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/analytics/notify-reach")
      .then(({ data }) => {
        if (mounted) setData(data);
      })
      .catch(() => {
        if (mounted) setData(null);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (loading || !data) return null;

  const total = Number(data.total_reachable || 0);
  const followers = Number(data.total_followers || 0);
  const perTruck = Array.isArray(data.per_truck) ? data.per_truck : [];
  const spark = Array.isArray(data.spark) && data.spark.length === 7 ? data.spark : null;
  const delta = data.spark_delta_pct;

  // Conversion rate: of people following at least one of the owner's
  // trucks, what share have push notifications enabled? Useful diagnostic
  // for the "your followers are missing pings" share-prompt copy.
  const followerToReachRate = followers > 0 ? Math.round((total / followers) * 100) : null;

  // Spark has any meaningful signal? (Don't render an empty flat line.)
  const sparkHasSignal = spark && spark.some((v) => v > 0);

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="notify-reach-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex-none h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <BellRinging size={20} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Notification reach
          </div>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            <div
              className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-none"
              data-testid="notify-reach-total"
            >
              {total}
            </div>
            <div className="text-sm text-muted-foreground leading-snug">
              {total === 1
                ? "person will get a ping when you go live"
                : "people will get a ping when you go live"}
            </div>
          </div>
          {followers > 0 && (
            <div className="mt-2 text-xs text-muted-foreground" data-testid="notify-reach-rate">
              {total} of {followers} {followers === 1 ? "follower" : "followers"} have notifications on
              {followerToReachRate != null && (
                <span className="ml-1.5 inline-flex items-center px-1.5 h-4 rounded-full bg-muted text-[10px] font-semibold tabular-nums text-foreground/80">
                  {followerToReachRate}%
                </span>
              )}
            </div>
          )}
        </div>
        {/* 7-day reach sparkline — top-right of the headline row, mirrors
            the AnalyticsCard sparkline pattern so the dashboard feels
            cohesive. Hidden when the entire window is zero (a flat empty
            line is louder than nothing). */}
        {sparkHasSignal && (
          <div
            className="flex-none flex flex-col items-end justify-start min-w-[88px]"
            data-testid="notify-reach-spark"
          >
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              7-day
            </div>
            <div className="text-primary">
              <Sparkline values={spark} width={88} height={28} />
            </div>
            {delta != null && (
              <div className="mt-1 inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums">
                {delta === "new" ? (
                  <span
                    className="inline-flex items-center px-1.5 h-4 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    data-testid="notify-reach-delta"
                  >
                    NEW
                  </span>
                ) : delta === 0 ? (
                  <span
                    className="inline-flex items-center px-1.5 h-4 rounded-full bg-muted text-muted-foreground"
                    data-testid="notify-reach-delta"
                  >
                    FLAT
                  </span>
                ) : delta > 0 ? (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    data-testid="notify-reach-delta"
                  >
                    <ArrowUp size={9} weight="bold" /> {delta}%
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-400"
                    data-testid="notify-reach-delta"
                  >
                    <ArrowDown size={9} weight="bold" /> {Math.abs(delta)}%
                  </span>
                )}
                <span className="text-muted-foreground ml-1 normal-case font-normal">vs 7d ago</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toggle — collapsed by default so the dashboard stays scannable.
          Owners can drill in when they want the breakdown. */}
      {(perTruck.length >= 1 || followers > 0) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 -mb-1 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition"
          data-testid="notify-reach-toggle"
        >
          <CaretDown
            weight="bold"
            className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          {expanded ? "Hide breakdown" : "Show breakdown"}
        </button>
      )}

      {expanded && (
        <>
      {/* Per-truck breakdown — only render if owner has 2+ trucks (otherwise
          the headline already says it all). */}
      {perTruck.length >= 2 && total > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            By truck
          </div>
          <ul className="space-y-1.5" data-testid="notify-reach-per-truck">
            {perTruck.map((row) => (
              <li
                key={row.truck_id}
                className="flex items-center justify-between gap-3 text-sm"
                data-testid={`notify-reach-row-${row.truck_id}`}
              >
                <span className="truncate text-foreground">{row.truck_name}</span>
                <span className="flex-none flex items-center gap-1.5 text-muted-foreground tabular-nums">
                  <BellRinging size={14} weight="fill" className="text-primary" />
                  <span className="font-semibold text-foreground">{row.reachable}</span>
                  <span className="text-xs">/ {row.followers}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coaching block — adapts to where the owner is in the funnel. */}
      <div className="mt-4 pt-4 border-t border-border">
        {total === 0 && followers === 0 ? (
          <div className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed">
            <Megaphone size={16} weight="duotone" className="text-primary flex-none mt-0.5" />
            <span>
              No reach yet. Tap{" "}
              <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                <ShareNetwork size={11} weight="duotone" /> Share
              </span>{" "}
              on your truck card and post the link to social — every follower
              who taps &ldquo;Notify me&rdquo; shows up here, and they&rsquo;ll get a ping the
              second you go live.
            </span>
          </div>
        ) : total === 0 && followers > 0 ? (
          <div className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed">
            <BellRinging size={16} weight="duotone" className="text-primary flex-none mt-0.5" />
            <span>
              You have <span className="font-semibold text-foreground">{followers} {followers === 1 ? "follower" : "followers"}</span>{" "}
              but none have turned on notifications yet. When you next go live, ask
              regulars to tap the bell on your truck card — every one bumps this number.
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed">
            <Megaphone size={16} weight="duotone" className="text-primary flex-none mt-0.5" />
            <span>
              Every share grows this number. Tap{" "}
              <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                <ShareNetwork size={11} weight="duotone" /> Share
              </span>{" "}
              on your truck card to grow it — more notifications mean more visits the
              second you&rsquo;re open.
            </span>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
